import { useState, useEffect, useCallback } from "react";
import { displays, screenUse } from "../constants/constants";

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/

  /* how much of the bigger dimension to use */

  const [width, widthSet] = useState(window.innerWidth);
  const [height, heightSet] = useState(window.innerHeight);
  const [mobileVertical, mobileVerticalSet] = useState(false);
  const [tabletVertical, tabletVerticalSet] = useState(false);
  const [tabletHorizontal, tabletHorizontalSet] = useState(false);
  const [desktop, desktopSet] = useState(false);
  const [mobileHorizontal, mobileHorizontalSet] = useState(false);
  const [horizontal, horizontalSet] = useState(false);
  const [vertical, verticalSet] = useState(false);
  const [currentDisplay, currentDisplaySet] = useState("desktop");
  const [appContentContainerWidth, appContentContainerWidthSet] = useState("");
  const [appContentContainerWidthValue, appContentContainerWidthValueSet] =
    useState(1);
  const [appContentContainerWidthUnit, appContentContainerWidthUnitSet] =
    useState("");
  const [appContentContainerHeight, appContentContainerHeightSet] =
    useState("");
  const [appContentContainerHeightValue, appContentContainerHeightValueSet] =
    useState(1);
  const [appContentContainerHeightUnit, appContentContainerHeightUnitSet] =
    useState("");

  /* custom height and width unit relative to AppContentContainer */
  const h = useCallback(
    (value: number) => {
      return `${((value * appContentContainerHeightValue) / 100).toFixed(
        2
      )}${appContentContainerHeightUnit}`;
    },
    [appContentContainerHeightUnit, appContentContainerHeightValue]
  );
  const w = useCallback(
    (value: number) => {
      return `${((value * appContentContainerWidthValue) / 100).toFixed(
        2
      )}${appContentContainerWidthUnit}`;
    },
    [appContentContainerWidthUnit, appContentContainerWidthValue]
  );

  // console.log(
  //   {
  //     width,
  //     height,
  //     mobileVertical,
  //     tabletVertical,
  //     tabletHorizontal,
  //     desktop,
  //     mobileHorizontal,
  //     horizontal,
  //     vertical,
  //     currentDisplay,
  //     appContentContainerWidth,
  //     appContentContainerWidthValue,
  //     appContentContainerWidthUnit,
  //     appContentContainerHeight,
  //     appContentContainerHeightValue,
  //     appContentContainerHeightUnit,
  //   },
  //   "h(10)",
  //   h(10),
  //   "h(100)",
  //   h(100),
  //   "w(10)",
  //   w(10),
  //   "w(100)",
  //   w(100)
  // );

  useEffect(() => {
    //reset all
    mobileVerticalSet(false);
    tabletVerticalSet(false);
    tabletHorizontalSet(false);
    desktopSet(false);
    mobileHorizontalSet(false);
    horizontalSet(false);
    verticalSet(false);
    currentDisplaySet("desktop");
    appContentContainerWidthSet("");
    appContentContainerWidthValueSet(1);
    appContentContainerWidthUnitSet("");
    appContentContainerHeightSet("");
    appContentContainerHeightValueSet(1);
    appContentContainerHeightUnitSet("");
    // compute and set values
    if (width / height < 0.7) {
      mobileVerticalSet(true);
      currentDisplaySet(displays.mobileVertical);
    }
    if (width / height >= 0.7 && width / height < 1) {
      tabletVerticalSet(true);
      currentDisplaySet(displays.tabletVertical);
    }
    if (width / height >= 1 && width / height < 1.4) {
      tabletHorizontalSet(true);
      currentDisplaySet(displays.tabletHorizontal);
    }
    if (width / height >= 1.4 && width / height < 1.7) {
      desktopSet(true);
      currentDisplaySet(displays.desktop);
    }
    if (width / height >= 1.7) {
      mobileHorizontalSet(true);
      currentDisplaySet(displays.mobileHorizontal);
    }

    if (width / height < 1) {
      verticalSet(true);
      if (width / height >= 9 / 16) {
        // set width
        appContentContainerWidthSet(`${((9 / 16) * screenUse).toFixed(2)}vh`);
        appContentContainerWidthValueSet(
          Number(((9 / 16) * screenUse).toFixed(2))
        );
        appContentContainerWidthUnitSet("vh");
        // set height
        appContentContainerHeightSet(`${screenUse}vh`);
        appContentContainerHeightValueSet(screenUse);
        appContentContainerHeightUnitSet("vh");
      }
      if (width / height < 9 / 16) {
        // set width
        appContentContainerWidthSet(`${screenUse}vw`);
        appContentContainerWidthValueSet(screenUse);
        appContentContainerWidthUnitSet("vw");
        // set height
        appContentContainerHeightSet(`${((16 / 9) * screenUse).toFixed(2)}vw`);
        appContentContainerHeightValueSet(
          Number(((16 / 9) * screenUse).toFixed(2))
        );
        appContentContainerHeightUnitSet("vw");
      }
    }
    if (width / height >= 1) {
      horizontalSet(true);
      if (width / height >= 16 / 9) {
        // set width
        appContentContainerWidthSet(`${((16 / 9) * screenUse).toFixed(2)}vh`);
        appContentContainerWidthValueSet(
          Number(((16 / 9) * screenUse).toFixed(2))
        );
        appContentContainerWidthUnitSet("vh");
        // set height
        appContentContainerHeightSet(`${screenUse}vh`);
        appContentContainerHeightValueSet(screenUse);
        appContentContainerHeightUnitSet("vh");
      }
      if (width / height < 16 / 9) {
        // set width
        appContentContainerWidthSet(`${screenUse}vw`);
        appContentContainerWidthValueSet(screenUse);
        appContentContainerWidthUnitSet("vw");
        // set height
        appContentContainerHeightSet(`${((9 / 16) * screenUse).toFixed(2)}vw`);
        appContentContainerHeightValueSet(
          Number(((9 / 16) * screenUse).toFixed(2))
        );
        appContentContainerHeightUnitSet("vw");
      }
    }
  }, [width, height]);

  useEffect(() => {
    // Handler to call on window resize

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      widthSet(width);
      heightSet(height);
    };
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return {
    mobileVertical,
    tabletVertical,
    tabletHorizontal,
    desktop,
    mobileHorizontal,
    currentDisplay,
    vertical,
    horizontal,
    appContentContainerWidth,
    appContentContainerWidthValue,
    appContentContainerWidthUnit,
    appContentContainerHeight,
    appContentContainerHeightValue,
    appContentContainerHeightUnit,
    h,
    w,
  };
}
